/**
 * TrueRoasWidget — paid-invoice revenue ÷ Google Ads spend.
 *
 * This is the differentiator that off-the-shelf tools structurally cannot
 * build: Optmyzr, Adalysis et al. see Google Ads conversion *value*
 * (which Google guesses from the pixel/imported events), but only Web
 * Construct sees actual money paid into the bank — invoices marked PAID
 * against the bound customer. Dividing the two gives a ROAS that reflects
 * the real business outcome, not an attribution model.
 *
 * Scope for Phase 3:
 *   - Single 30-day window. 60/90 day windows + a custom date range come
 *     later (Phase 4) when we add a date-range picker.
 *   - Reads invoices from useInvoice() — the same context the rest of
 *     the customer panel uses, so we get realtime updates for free.
 *   - Pulls Ads spend via callAdsQuery so we don't depend on the parent
 *     tab having already loaded its daily data — keeps the widget
 *     embeddable elsewhere (e.g. customer overview tab, Phase 4 home
 *     dashboard portfolio view).
 *
 * Edge cases handled:
 *   - No paid invoices in window: shows revenue 0, ROAS as em-dash with
 *     spend pure-loss caption.
 *   - No spend in window: shows revenue but ROAS as ∞ when revenue > 0
 *     (not divide-by-zero), em-dash when both are 0.
 *   - Bound CID is empty: renders nothing (parent already showed the
 *     bind CTA).
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useAppData } from '../../../providers/AppDataProvider';
import {
  DailyPerformanceRow,
  callAdsQuery,
  microsToAmount,
} from '../../marketing/googleAdsApi';

interface TrueRoasWidgetProps {
  /** Customer doc id (used to filter invoices by contactId). */
  customerId: string;
  /** Bound Google Ads customer id (digits only). */
  adsCustomerId: string;
  /** Currency code from the Ads customer summary, for formatting. */
  adsCurrency?: string;
}

const WINDOW_DAYS = 30;

function formatCurrency(amount: number, currency?: string): string {
  if (!currency) {
    return new Intl.NumberFormat('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);
  }
  try {
    return new Intl.NumberFormat('en-AU', { style: 'currency', currency, minimumFractionDigits: 2 }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
}

function startOfWindow(): Date {
  const d = new Date();
  d.setDate(d.getDate() - WINDOW_DAYS);
  d.setHours(0, 0, 0, 0);
  return d;
}

/** Parse an invoice date (YYYY-MM-DD or a Firestore-ish string) into a
 *  Date safely. Returns null on anything unparseable so the filter can
 *  drop it cleanly. */
function parseInvoiceDate(value: string | undefined): Date | null {
  if (!value) return null;
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

export const TrueRoasWidget: React.FC<TrueRoasWidgetProps> = ({ customerId, adsCustomerId, adsCurrency }) => {
  // useAppData provides invoices in the Xero-shape (XeroInvoice[]) which
  // is already loaded by the parent ClientDetailPanel — avoids forcing
  // an InvoiceProvider wrapper around this widget's parents and prevents
  // a duplicate Firestore listener.
  const { invoices } = useAppData();
  const [dailySpend, setDailySpend] = useState<DailyPerformanceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ── Ads spend over the window ─────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    if (!adsCustomerId) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    void (async () => {
      const resp = await callAdsQuery<DailyPerformanceRow>(adsCustomerId, 'daily_performance_last_30_days');
      if (cancelled) return;
      if (!resp.ok) {
        setError(resp.error || 'Could not load Ads spend');
        setDailySpend([]);
      } else {
        setDailySpend(resp.rows || []);
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [adsCustomerId]);

  // ── Revenue from paid invoices in window ──────────────────────
  // AmountPaid is used (not Total) so partially-paid invoices count
  // their actual cash receipts. Status === 'PAID' is the canonical
  // signal — partially-paid SENT invoices are excluded to avoid
  // overstating "completed" revenue. XeroInvoice shape uses
  // PascalCase + nested Contact.ContactID.
  const revenueAndCount = useMemo(() => {
    if (!customerId) return { revenue: 0, count: 0 };
    const cutoff = startOfWindow();
    let revenue = 0;
    let count = 0;
    for (const inv of invoices as any[]) {
      if (!inv?.Contact || inv.Contact.ContactID !== customerId) continue;
      if (String(inv.Status || '').toUpperCase() !== 'PAID') continue;
      const dStr: string | undefined = inv.DateString || inv.Date;
      const d = parseInvoiceDate(dStr);
      if (!d || d < cutoff) continue;
      revenue += Number(inv.AmountPaid) || Number(inv.Total) || 0;
      count += 1;
    }
    return { revenue, count };
  }, [invoices, customerId]);

  const spend = useMemo(() => {
    let total = 0;
    for (const row of dailySpend) total += microsToAmount(row.metrics?.costMicros);
    return total;
  }, [dailySpend]);

  // ── ROAS classification ───────────────────────────────────────
  // Four states with different visual treatments so the user can read
  // the situation at a glance.
  const { roasLabel, roasTone, captionLine } = useMemo(() => {
    const rev = revenueAndCount.revenue;
    if (loading) return { roasLabel: '…', roasTone: 'muted', captionLine: 'loading…' };
    if (spend === 0 && rev === 0) {
      return { roasLabel: '—', roasTone: 'muted', captionLine: 'No Ads spend and no paid invoices in this window.' };
    }
    if (spend === 0 && rev > 0) {
      return { roasLabel: '∞', roasTone: 'success', captionLine: 'Revenue with no Ads spend — all gain.' };
    }
    if (rev === 0) {
      return { roasLabel: '0.00x', roasTone: 'danger', captionLine: 'Ads spend with no paid invoices in this window.' };
    }
    const ratio = rev / spend;
    const tone = ratio >= 3 ? 'success' : ratio >= 1.5 ? 'primary' : ratio >= 1 ? 'warning' : 'danger';
    const caption =
      ratio >= 3 ? 'Healthy — for every $1 spent, $' + ratio.toFixed(2) + ' returned.' :
      ratio >= 1 ? 'Above break-even — for every $1 spent, $' + ratio.toFixed(2) + ' returned.' :
      'Below break-even — for every $1 spent, only $' + ratio.toFixed(2) + ' returned.';
    return { roasLabel: `${ratio.toFixed(2)}x`, roasTone: tone, captionLine: caption };
  }, [loading, spend, revenueAndCount.revenue]);

  if (!adsCustomerId) return null;

  return (
    <div className="card mb-5 true-roas-widget">
      <div className="card-header">
        <h5 className="card-title fw-bold m-0">
          <i className="bi bi-arrow-left-right me-2"></i>
          True ROAS · last {WINDOW_DAYS} days
        </h5>
        <div className="card-toolbar">
          <span className="text-muted fs-8">paid invoices ÷ Ads spend</span>
        </div>
      </div>
      <div className="card-body">
        {error && (
          <div className="alert alert-light-warning py-2 px-3 mb-3">
            <i className="bi bi-exclamation-triangle me-2"></i>{error}
          </div>
        )}
        <div className="row g-3">
          <div className="col-md-4">
            <div className="text-muted fs-8 text-uppercase mb-1">Ads spend</div>
            <div className="fs-2 fw-bold">{loading ? '…' : formatCurrency(spend, adsCurrency)}</div>
            <div className="text-muted fs-8">{dailySpend.length} days of activity</div>
          </div>
          <div className="col-md-4">
            <div className="text-muted fs-8 text-uppercase mb-1">Revenue (paid invoices)</div>
            <div className="fs-2 fw-bold">{formatCurrency(revenueAndCount.revenue, adsCurrency)}</div>
            <div className="text-muted fs-8">
              {revenueAndCount.count} paid invoice{revenueAndCount.count === 1 ? '' : 's'} in window
            </div>
          </div>
          <div className="col-md-4">
            <div className="text-muted fs-8 text-uppercase mb-1">True ROAS</div>
            <div className={`fs-2 fw-bold text-${roasTone}`}>{roasLabel}</div>
            <div className="text-muted fs-8">{captionLine}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrueRoasWidget;
